import { useState } from "react";
import type { SceneChoice } from "../types/story";
import { sound } from "../lib/sound";
import { hapticTick } from "../lib/haptics";

/**
 * MOMENT 4 — the choice as a held breath.
 *
 * The story leans in to ask. The paths are presented as two arched DOORWAYS
 * into the evening — light spilling under each door, a crack of lamplight up
 * the middle that widens as a finger nears — never as buttons floating on top.
 * The whole stage dims and waits (App adds `app--choosing`). When the child
 * picks, the chosen door floods with light and BLOOMS — a soft confirming note
 * and a single gentle haptic tick (one of exactly two moments haptics are
 * earned) — and only then, a beat later, does the page turn toward it.
 * Agency as intimacy, not a menu.
 *
 * Shown only at a scene boundary in interactive mode. Large tap targets; the
 * doorway art is pure CSS (no authored assets needed, nothing generated).
 */
const BLOOM_MS = 460;

export function ChoicePrompt({
  choice,
  onChoose,
}: {
  choice: SceneChoice;
  onChoose: (optionIndex: number) => void;
}) {
  const [chosen, setChosen] = useState<number | null>(null);

  function pick(i: number) {
    if (chosen !== null) return; // one path, once
    setChosen(i);
    sound.choiceConfirm();
    hapticTick();
    // Let the chosen path bloom before the story turns toward it.
    window.setTimeout(() => onChoose(i), BLOOM_MS);
  }

  return (
    <div className="choice">
      <p className="choice-prompt">{choice.prompt}</p>
      <div className="choice-paths">
        {choice.options.map((opt, i) => {
          const tone = i % 2 ? "cool" : "warm";
          const state =
            chosen === null ? "" : i === chosen ? " is-chosen" : " is-dimmed";
          return (
            <button
              key={i}
              type="button"
              className={`choice-path choice-path--${tone}${state}`}
              style={{ animationDelay: `${i * 130}ms` }}
              onClick={() => pick(i)}
              disabled={chosen !== null}
            >
              <span className="choice-path__glow" aria-hidden="true" />
              <span className="choice-path__label">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
